import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { FaAngleLeft } from "react-icons/fa6";
import { useTranslation } from "react-i18next";

function MoodNote() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const textareaRef = useRef(null);

  const maxLength = 1200;
  const maxLengthBar = 200; // Ціль для візуального заповнення смуги

  const [text, setText] = useState(localStorage.getItem("moodNote") || "");
  const [selectedMood, setSelectedMood] = useState(3);
  const [sparks, setSparks] = useState([]);
  const [placeholder, setPlaceholder] = useState("");
  const [goalReached, setGoalReached] = useState(false);

  // 1. Відновлення настрою
  useEffect(() => {
    const mood = localStorage.getItem("selectedMood");
    if (!mood) {
      navigate("/");
    } else {
      setSelectedMood(parseInt(mood, 10) || 3);
    }
  }, [navigate]);

  // 2. Вибір випадкового плейсхолдера
  useEffect(() => {
    const moodPlaceholders = t(`writeYourThoughts.${selectedMood}`, {
      returnObjects: true,
    });
    if (Array.isArray(moodPlaceholders) && moodPlaceholders.length > 0) {
      setPlaceholder(
        moodPlaceholders[Math.floor(Math.random() * moodPlaceholders.length)],
      );
    }
  }, [selectedMood, t]);

  // 3. Збереження тексту
  useEffect(() => {
    if (text.trim() !== "") {
      localStorage.setItem("moodNote", text.trim());
    } else {
      localStorage.removeItem("moodNote");
    }
  }, [text]);

  const progress = Math.min((text.length / maxLengthBar) * 100, 100);

  // 4. Логіка генерації іскорок та ВАУ-ефекту
  useEffect(() => {
    if (text.length === 0) return;

    // Перевірка на ВАУ-ефект (досягнуто 200 символів)
    if (progress === 100 && !goalReached) {
      setGoalReached(true);

      // Вибух з 50 іскорок в усі боки
      const wowSparks = Array.from({ length: 50 }).map(() => ({
        id: Date.now() + Math.random(),
        left: "100%",
        tx: (Math.random() - 0.5) * 300, // Широкий розліт по X
        ty: (Math.random() - 0.5) * 300, // Широкий розліт по Y
        duration: 0.8 + Math.random() * 0.7,
        scale: 1 + Math.random() * 1.5,
        color: ["#fbbf24", "#fcd34d", "#34d399", "#a5f3fc"][
          Math.floor(Math.random() * 4)
        ], // Золоті, смарагдові, блакитні
      }));
      setSparks((prev) => [...prev, ...wowSparks].slice(-80));
    } else if (progress < 100) {
      // Звичайні іскорки при наборі тексту
      if (goalReached) setGoalReached(false); // Скидаємо золотий статус, якщо юзер стер текст

      const normalSparks = Array.from({ length: 2 }).map(() => ({
        id: Date.now() + Math.random(),
        left: `${progress}%`,
        tx: (Math.random() - 0.5) * 40,
        ty: -20 - Math.random() * 40, // Летять переважно вгору
        duration: 0.5 + Math.random() * 0.5,
        scale: 0.8 + Math.random() * 0.5,
        color: "#a5f3fc",
      }));
      setSparks((prev) => [...prev, ...normalSparks].slice(-30));
    }
  }, [text.length, progress, goalReached]);

  // Очищення старих іскорок, щоб не перевантажувати DOM
  useEffect(() => {
    if (sparks.length > 0) {
      const timer = setTimeout(() => setSparks([]), 2000);
      return () => clearTimeout(timer);
    }
  }, [sparks]);

  return (
    <div className="flex flex-col flex-1 h-full overflow-hidden bg-[#061014] relative select-none font-sans text-white -m-4 p-4">
      {/* --- CSS АНІМАЦІЇ --- */}
      <style>{`
        @keyframes flySpark {
          0% { opacity: 1; transform: translate(0, 0) scale(1); }
          100% { opacity: 0; transform: translate(var(--tx), var(--ty)) scale(var(--scale)); }
        }
      `}</style>

      {/* --- ФОН --- */}
      <div
        className="absolute inset-0 pointer-events-none z-0"
        style={{
          background:
            "radial-gradient(circle at 50% 100%, oklch(30.2% 0.056 229.695) 0%, #061014 60%)",
        }}
      />
      <div className="absolute inset-0 opacity-20 bg-[url('https://www.transparenttextures.com/patterns/stardust.png')] pointer-events-none z-0"></div>

      {/* --- ШАПКА --- */}
      <div className="relative z-50 w-full pt-6 pb-2 px-20">
        <p className="text-2xl font-semibold text-center text-white truncate w-full">
          {t("moods.makeNote")}
        </p>
      </div>

      <div className="relative z-10 flex flex-col flex-1 gap-y-5 mt-5">
        {/* --- ПРОГРЕС-БАР З ІСКРАМИ --- */}
        <div className="relative w-full h-3 bg-white/10 rounded-full overflow-visible border border-white/5 transition-all">
          {/* Смуга заповнення */}
          <div
            className={`h-full relative rounded-full bg-gradient-size transition-all duration-300 ease-out ${
              goalReached
                ? "bg-gradient-to-tr from-amber-400 via-yellow-200 to-amber-500 shadow-[0_0_20px_rgba(251,191,36,0.8)] animate-pulse"
                : "bg-gradient-to-tr from-emerald-500 via-cyan-500 to-emerald-500 shadow-[0_0_10px_rgba(52,211,153,0.5)]"
            }`}
            style={{ width: `${progress}%` }}
          >
            {/* Головка прогрес-бару (яскрава крапка зміщена правіше) */}
            {progress > 0 && (
              <div
                className={`absolute -right-1.5 top-1/2 -translate-y-1/2 w-4 h-4 rounded-full blur-[1px] ${
                  goalReached
                    ? "bg-yellow-200 shadow-[0_0_15px_#fef08a] scale-125 transition-transform"
                    : "bg-white shadow-[0_0_10px_white]"
                }`}
              />
            )}
          </div>

          {/* Літаючі іскри */}
          {sparks.map((spark) => (
            <div
              key={spark.id}
              className="absolute top-1/2 -translate-y-1/2 rounded-full pointer-events-none"
              style={{
                left: spark.left,
                width: "6px",
                height: "6px",
                backgroundColor: spark.color,
                boxShadow: `0 0 10px ${spark.color}`,
                "--tx": `${spark.tx}px`,
                "--ty": `${spark.ty}px`,
                "--scale": spark.scale,
                animation: `flySpark ${spark.duration}s cubic-bezier(0.25, 1, 0.5, 1) forwards`,
              }}
            />
          ))}
        </div>

        {/* --- ПОЛЕ ВВОДУ ТЕКСТУ (Glassmorphism) --- */}
        <div
          className={`relative flex-1 bg-white/5 border p-5 rounded-3xl shadow-inner backdrop-blur-md transition-all duration-500 focus-within:bg-white/10 ${
            goalReached
              ? "border-amber-500/50 focus-within:border-amber-400/80 shadow-[inset_0_0_20px_rgba(251,191,36,0.1)]"
              : "border-emerald-900/50 focus-within:border-emerald-500/50"
          }`}
        >
          <textarea
            ref={textareaRef}
            className="w-full h-full bg-transparent text-white text-lg placeholder-emerald-100/30 resize-none outline-none custom-scrollbar"
            placeholder={placeholder}
            value={text}
            maxLength={maxLength}
            onChange={(e) => setText(e.target.value)}
          />

          {/* Лічильник ліміту */}
          {text.length >= maxLength * 0.8 && (
            <div className="absolute bottom-4 right-4 text-xs font-bold tracking-widest bg-black/40 text-red-400 px-3 py-1.5 rounded-xl border border-red-500/20 shadow-lg animate-fade-in">
              {text.length} / {maxLength}
            </div>
          )}
        </div>
      </div>

      {/* --- НИЖНІЙ БЛОК --- */}
      <div className="relative z-20 mt-auto pt-4 pb-2 flex justify-between">
        <Link
          to="/mood-activities"
          className="block text-center text-3xl font-bold rounded-lg py-3 w-full shadow-[0_0_20px_rgba(16,185,129,0.3)] transition-all duration-300 bg-gradient-to-tr from-emerald-500 via-cyan-500 to-emerald-500 bg-gradient-size animate-gradient-move text-white"
        >
          {text.trim() ? t("continue") : t("skip")}
        </Link>
      </div>
    </div>
  );
}

export default MoodNote;
